import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { CUSTOMER_FORM_ID } from 'global/constants';
import { deepPrune } from '../utils/object/prune';
import { RootState } from '../global';
import { BuyOrderApiResponse } from '../components/admin/buy-process/buy-order-detailed/buy-order-detailed-types';
import { getAuthToken } from '../components/app/components/auth/auth';
import api from '../global/api';
import {
  initBuyOrderPeople,
  saveBuyOrderPeople,
} from '../components/admin/buy-process/people/people-redux';
import { saveBuyOrder } from '../components/admin/buy-process/buy-order-edit/buy-order-edit-redux';
import {
  initBuyOrderOrganizations,
  saveBuyOrderOrganizations,
} from '../components/admin/buy-process/organizations/organizations-redux';
import { SellOrderDetailedApiResponse } from '../utils/sell-process/sell-process-detailed-types';
import {
  initSellPeople,
  saveSellOrderPeople,
} from '../components/admin/sell-process/components/sell-process-people/sell-process-people-redux';
import {
  initSellOrganizations,
  saveSellOrderOrganizations,
} from '../components/admin/sell-process/components/sell-process-organizations/sell-process-organizations-redux';
import { saveSellOrder } from '../components/admin/sell-process/sell-process-redux';
import { selectBuyUpdateRequestFactory } from './buy-process/update-order/selectors';
import { selectBuyDeleteRelationsFactory } from './buy-process/selectors';
import { selectSellUpdateRequestFactory } from './sell-process/update-order/selectors';
import { saveSellOrderBanks } from './sell-process/banks/reducer';
import {
  BankType,
  FetchOrderBanksApiResponse,
} from './sell-process/banks/types';

// TODO: Add buy/sell removal from redux
export const reduxApi = createApi({
  reducerPath: 'api',
  tagTypes: ['BuyOrder', 'SellOrder'],
  refetchOnMountOrArgChange: true,
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
    prepareHeaders: async (headers) => {
      const token = await getAuthToken();

      if (token) {
        headers.set('Authorization', token);
      }

      return headers;
    },
  }),
  endpoints: (builder) => ({
    updateBuyOrder: builder.mutation<{ id: string }, { orderId: string }>({
      invalidatesTags(response) {
        return response
          ? [{ type: 'BuyOrder', id: response.id }]
          : ['BuyOrder'];
      },
      queryFn: async ({ orderId }, { getState }) => {
        // Quick fix to avoid sending PUT requests on every buy order step - TODO better solution
        if (orderId === CUSTOMER_FORM_ID) {
          return { data: { id: orderId } };
        }
        const buyOrder = selectBuyUpdateRequestFactory(orderId)(
          (await getState()) as RootState,
        );

        const response = await api.put<{ id: string }>(
          `/orders/buy/edit/${orderId}`,
          // TODO - improve the types of deepPrune to avoid as any
          // I know that id is a bit too general key, but Ids are only sent if there is update.
          // And there is update id should always be present, otherwise (create) it will be removed from the request
          deepPrune(buyOrder, [
            'client',
            'auditor',
            'legalShareholders',
            'co',
            'organizationId',
            'id',
          ] as any),
        );

        const deleteRequest = selectBuyDeleteRelationsFactory(orderId)(
            (await getState()) as RootState,
        ) ?? [];

        if (deleteRequest.length > 0) {
          await api.delete(`/orders/buy/relations/${orderId}`, {
            data: {
              deleteRequest,
            },
          });
        }

        return { data: { id: response.data.id } };
      },
    }),

    getBuyOrder: builder.query<BuyOrderApiResponse, { id: string }>({
      providesTags: (response) => [
        {
          type: 'BuyOrder',
          ...(response && { id: response.id }),
        },
      ],
      queryFn: async ({ id }, { dispatch }) => {
        const { data } = await api.get<BuyOrderApiResponse>(
          `/orders/buy/${id}`,
        );

        dispatch([
          initBuyOrderPeople({ id }),
          saveBuyOrderPeople({ id, buyOrder: data }),
        ]);

        dispatch([
          initBuyOrderOrganizations({ id }),
          saveBuyOrderOrganizations({ id, buyOrder: data }),
        ]);

        dispatch(
          saveBuyOrder({
            id,
            buyOrder: data,
          }),
        );

        return { data };
      },
    }),

    getSellOrder: builder.query<SellOrderDetailedApiResponse, { id: string }>({
      providesTags: (response) => [
        { type: 'SellOrder', ...(response && { id: response.id }) },
      ],
      queryFn: async ({ id }, { dispatch }) => {
        const [{ data }, { data: banks }] = await Promise.all([
          api.get<SellOrderDetailedApiResponse>(`/orders/sell/${id}`),
          api.get<FetchOrderBanksApiResponse>(`/banks/order/${id}`),
        ]);
        dispatch([
          initSellPeople({ id }),
          saveSellOrderPeople({ id, sellOrder: data }),
        ]);

        dispatch([
          initSellOrganizations({ id }),
          saveSellOrderOrganizations({ id, sellOrder: data }),
        ]);

        dispatch([
          saveSellOrder({
            id,
            sellOrder: data,
          }),
        ]);

        dispatch(saveSellOrderBanks({ orderId: id, banks }));

        return { data };
      },
    }),
    searchBanks: builder.query<BankType[], { bankName: string }>({
      queryFn: async ({ bankName }) =>
        api.get<BankType[]>(`/banks/search?bankName=${bankName}`),
    }),

    updateSellOrder: builder.mutation<{ id: string }, { orderId: string }>({
      invalidatesTags(response) {
        return response
          ? [{ type: 'SellOrder', id: response.id }]
          : ['SellOrder'];
      },
      queryFn: async ({ orderId }, { getState }) => {
        if (orderId === CUSTOMER_FORM_ID) {
          return { data: { id: orderId } };
        }

        const store = (await getState()) as RootState;

        const { deleteRequest } = store.sellOrder[orderId];
        const sellOrderUpdate = selectSellUpdateRequestFactory(orderId)(store);

        const response = await api.put<{ id: string }>(
          `/orders/sell/edit/${orderId}`,
          sellOrderUpdate,
        );

        if (deleteRequest && deleteRequest.length !== 0) {
          await api.delete(`/orders/sell/relations/${orderId}`, {
            data: {
              deleteRequest,
            },
          });
        }

        return { data: { id: response.data.id } };
      },
    }),
  }),
});

export const {
  useUpdateBuyOrderMutation,
  useGetBuyOrderQuery,
  useSearchBanksQuery,
  useGetSellOrderQuery,
  useUpdateSellOrderMutation,
} = reduxApi;
